import { Clear } from '@mui/icons-material';
import {
    Alert,
    Button,
    Checkbox,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    IconButton,
    InputAdornment,
    List,
    ListItem,
    ListItemButton,
    ListItemText,
    Snackbar,
    TextField,
    Tooltip,
    Typography
} from '@mui/material';
import React, { useState } from 'react';
import Log from '@/Logger';
import { kintoneErrorMessage } from './errorMessage';
import { getKintoneAppsList } from './helper';

/**
 * ユーザのkintone環境にあるkintoneアプリのオブジェクト (項目はkintone使用に準拠: https://cybozu.dev/ja/kintone/docs/rest-api/apps/get-apps/)
 * descriptionはユーザが設定しないことがあるので任意項目にしている
 */
export type kintoneApp = {
    id: number;
    name: string;
    description?: string;
    createdAt: string;
    modifiedAt: string;
};

type KintoneDialogProps = {
    open: boolean;
    onClose: () => void;
    onSelect: (selectedItems: kintoneApp[]) => void; // 複数選択対応
    maxSelectableNum: number; //アプリの選択可能数
    dialogTitle: string; //ダイアログのタイトル(太字で上に表示される)
    dialogDescription: string; //ダイアログの説明(平文字で表示される)
};

/**
 * ユーザのkintone環境上のアプリ一覧を表示するダイアログのコンポーネント
 * 検索バー、アプリリスト、キャンセルボタン、登録ボタンから成り、登録ボタンを押すと親コンポーネントに選択したアプリをItemの配列で送る。
 */
export default function KintoneDialog({
    open,
    onClose,
    onSelect,
    maxSelectableNum,
    dialogTitle,
    dialogDescription
}: KintoneDialogProps): React.ReactElement {
    const logger = Log.getLogger();
    const [searchTerm, setSearchTerm] = useState('');
    const [selectedItems, setSelectedItems] = useState<kintoneApp[]>([]);
    const [kintoneAppsList, setKintoneAppsList] = useState<kintoneApp[]>([]);
    const [loading, setLoading] = useState<boolean>(false); // ローディング状態
    const [snackBarOpen, setSnackBarOpen] = React.useState(false);
    const [snackBarText, setSnackBarText] = React.useState('');
    const [snackBarType, setSnackBarType] = React.useState<any>(undefined);

    //アプリ選択ボタンが開かれたときに実行
    React.useEffect(() => {
        if (open) {
            getKintoneApps();
        }
    }, [open]); // TODO: kintoneAppListが空の時のみgetした方がいい

    const handleSnackBarClose = (event?: React.SyntheticEvent | Event, reason?: string) => {
        if (reason === 'clickaway') {
            return;
        }
        setSnackBarOpen(false);
    };

    //再帰的にkintoneのAPIを呼び出し、ユーザ環境のアプリ一覧を取得する
    async function getKintoneApps() {
        const kintoneApps: kintoneApp[] = []

        //一度に百件のアプリしか取れないため、レスポンスがなくなるまで繰り返しAPIコールを行う(アプリの最大値は1000個なので最大11回のコールを行う)
        let offset = 0;
        const step = 100;

        setLoading(true); // ローディング開始
        await fetchKintoneApps()

        async function fetchKintoneApps() {
            logger.ui.info('SendDocument', 'kintoneFetch', 'Start fetching kintoneApps')

            getKintoneAppsList(offset)
                .then((apps) => {
                    if (apps.length === 0) {
                        // 終了条件：レスポンスが空になるまで行う
                        logger.ui.info('SendDocument', 'kintoneFetch', 'Finish fetching kintoneApps')
                        setKintoneAppsList(kintoneApps);
                        setLoading(false);
                        return;
                    }

                    // レスポンスをマッピングしてItem形式の配列に追加
                    const respObj: kintoneApp[] = apps.map((app) => ({
                        id: Number(app.kintoneAppId),
                        name: app.kintoneAppName,
                        description: app.kintoneAppDescription || undefined,
                        createdAt: app.kintoneAppCreatedAt,
                        modifiedAt: app.kintoneAppModifiedAt,
                    }));
                    kintoneApps.push(...respObj);

                    // オフセットを増やして再帰的に呼び出し
                    offset += step;
                    fetchKintoneApps();
                })
                .catch((e) => {
                    //ユーザーがkintoneと連携したまま管理者がテナント設定を解除すると、ここでエラーが起きるようになる
                    //更新がない場合は一定期間でユーザー登録も解除される(ttl設定)
                    logger.ui.error('SendDocument', 'kintoneTransition', 'Failed to fetch kintoneApps');

                    setSnackBarType('error');
                    setSnackBarText(kintoneErrorMessage.notRegisteredTenantSetting);
                    setSnackBarOpen(true);

                    setLoading(false);
                    throw e;
                });
        }

    }

    // 検索フィルター
    const filteredItems = kintoneAppsList.filter((item) =>
        item.name.toLowerCase().includes(searchTerm.toLowerCase())
    );

    const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        setSearchTerm(event.target.value);
    };

    const toggleSelection = (item: kintoneApp) => {
        if (selectedItems.some((selected) => selected.id === item.id)) {
            // 既に選択されている場合は削除
            logger.ui.info('kintoneAppsDialog', 'Selected App', 'delete selected App')
            setSelectedItems(selectedItems.filter((selected) => selected.id !== item.id));

        } else if (selectedItems.length < maxSelectableNum) {
            // 選択されていない場合は追加
            // 選択可能数を超える場合は何もしない
            logger.ui.info('kintoneAppsDialog', 'Selected App', 'Add new selected App')
            setSelectedItems([...selectedItems, item]);
        }
    };

    const handleConfirm = () => {
        onSelect(selectedItems); // 選択されたアイテムを親コンポーネントに送信
        logger.ui.info('kintoneAppsDialog', 'Handle confirm', `Confirm ${selectedItems.length} Apps`)
        onClose(); // ダイアログを閉じる
    };

    return (
        <>
            <Dialog open={open} onClose={onClose} fullWidth maxWidth='sm'>
                <DialogTitle>
                    <Typography variant='h6' fontWeight='bold'>
                        {dialogTitle}
                    </Typography>
                </DialogTitle>
                <Typography variant='body2' color='textSecondary' sx={{ marginBottom: 1, marginLeft: 5, marginRight: 5 }}>
                    {dialogDescription}
                </Typography>
                <DialogContent
                    sx={{
                        height: '70vh', // 縦方向を画面の70%に固定
                        overflowY: 'auto', // 必要に応じてスクロール可能にする
                    }}
                >
                    <TextField
                        fullWidth
                        margin='dense'
                        variant='outlined'
                        label='検索'
                        value={searchTerm}
                        onChange={handleSearchChange}
                        InputProps={{
                            endAdornment: searchTerm && (
                                <InputAdornment position='end'>
                                    <IconButton onClick={() => setSearchTerm('')} size='small'>
                                        <Clear />
                                    </IconButton>
                                </InputAdornment>
                            ),
                        }}
                    />
                    {loading ? (
                        // ローディング中の場合
                        <Typography variant='body1' align='center' sx={{ marginTop: 2 }}>
                            <CircularProgress />
                        </Typography>
                    ) : (
                        <List>
                            {filteredItems
                                .sort((a, b) => {
                                    //最終更新日が新しい順に表示(アプリの更新日のためレコード追加日時ではない)
                                    //ワイドコースのみ使えるAPIを使うとレコード追加日時でのソートも可能(https://cybozu.dev/ja/id/87b9a4d7093e6c78b1dff270/)
                                    const dateA = new Date(a.modifiedAt || 0).getTime();
                                    const dateB = new Date(b.modifiedAt || 0).getTime();
                                    return dateB - dateA;
                                })
                                .map((item) => (
                                    <ListItem key={item.id} disablePadding>
                                        <Tooltip
                                            title={//tooltipでアイテムの説明、作成日、最終更新日が出るようにする
                                                <>
                                                    <div><strong>説明:</strong> {item.description || 'なし'}</div>
                                                    <div><strong>作成日:</strong> {item.createdAt || 'なし'}</div>
                                                    <div><strong>最終更新日:</strong> {item.modifiedAt || 'なし'}</div>
                                                </>
                                            }
                                            placement='top'
                                        >
                                            <ListItemButton
                                                onClick={() => toggleSelection(item)}
                                                disabled={
                                                    !selectedItems.some((selected) => selected.id === item.id) &&
                                                    selectedItems.length >= maxSelectableNum
                                                } // 最大数を超えた場合、未選択のアイテムは無効化
                                            >
                                                <Checkbox
                                                    checked={selectedItems.some((selected) => selected.id === item.id)}
                                                />
                                                <ListItemText primary={item.name} />
                                            </ListItemButton>
                                        </Tooltip>
                                    </ListItem>
                                ))}
                        </List>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={onClose}>キャンセル</Button>
                    <Button onClick={handleConfirm} disabled={selectedItems.length === 0}>
                        確定
                    </Button>
                </DialogActions>

                <Snackbar open={snackBarOpen} onClose={handleSnackBarClose} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
                    <Alert onClose={handleSnackBarClose} severity={snackBarType} sx={{ width: '100%' }} >
                        {snackBarText}
                    </Alert>
                </Snackbar>
            </Dialog>

        </>

    );
};

// ダイアログのタイトル
export const TITLE = 'kintoneアプリ選択'